// PH-50 — moving this device from one shop to another, and noticing when another window did.
//
// A switch is deliberately a FULL page navigation to the target shop's own `/s/<slug>/` link,
// never an in-place React state change. That is what guarantees the ticket's "no stale data for
// even one render": every in-memory cart, customer, list and pending request of the old shop is
// thrown away with the page, and the new shop starts from exactly the path a person opening its
// link by hand takes — applyTenantResolution(), that shop's own config, that shop's PIN screen.

import { readKnownShops } from './knownShops';

// The device still holds sales the offline queue will replay on the next load. They carry no
// shop tag (offlineQueue.enqueueSale) and the backend books a replayed sale to whichever shop the
// session belongs to, so switching now would post shop A's unsent sales into shop B — the same
// hazard AccountSignIn.jsx already refuses a shop change for.
export class PendingSalesError extends Error {
  constructor(count, signedOut = false) {
    super('pending_sales');
    this.count = count;
    // True when the sale turned up only on the second look, after this shop's session had already
    // been ended: the device is then signed out of THIS shop too, and has to sign in again.
    this.signedOut = signedOut;
  }
}

// The offline queue could not be read at all. Not evidence it is empty, so the switch fails
// CLOSED on it — same rule and same reasoning as AccountSignIn.jsx's pendingSales().
export class QueueUnknownError extends Error {
  constructor(signedOut = false) {
    super('queue_unknown');
    this.signedOut = signedOut; // same meaning as on PendingSalesError
  }
}

export const switchErrorText = (err) => {
  if (err instanceof PendingSalesError && err.signedOut) {
    return `इस डिवाइस पर ${err.count} बिक्री अभी सिंक होनी बाकी है, इसलिए दुकान नहीं बदली गई। आप इस दुकान से लॉग आउट हो गए हैं — फिर से लोड करके PIN डालें; बिक्री अपने आप भेजी जाएगी / ${err.count} sale(s) on this device have not synced yet, so the shop was not switched. You have been signed out of this shop — reload and enter your PIN; the sales will send on their own`;
  }
  if (err instanceof PendingSalesError) {
    return `इस डिवाइस पर ${err.count} बिक्री अभी सिंक होनी बाकी है — पहले इंटरनेट से जोड़कर उन्हें भेज दें, फिर दुकान बदलें / ${err.count} sale(s) on this device have not synced yet — connect to the internet so they send, then switch shops`;
  }
  if (err instanceof QueueUnknownError && err.signedOut) {
    return 'इस डिवाइस पर बिना भेजी बिक्री की जाँच नहीं हो सकी, इसलिए दुकान नहीं बदली गई। आप इस दुकान से लॉग आउट हो गए हैं — फिर से लोड करके PIN डालें / Could not check this device for unsent sales, so the shop was not switched. You have been signed out of this shop — reload and enter your PIN';
  }
  if (err instanceof QueueUnknownError) {
    return 'इस डिवाइस पर बिना भेजी बिक्री की जाँच नहीं हो सकी, इसलिए दुकान नहीं बदली गई — पेज फिर से लोड करके कोशिश करें / Could not check this device for unsent sales, so the shop was not switched — reload the page and try again';
  }
  return 'दुकान नहीं बदली जा सकी — फिर से कोशिश करें / Could not switch shop — please try again';
};

/**
 * @param {string} slug  a shop already on this device's known-shops list
 * @param {{ storage?: Storage, getQueued: () => Promise<object[]>, logout: () => Promise<unknown>,
 *           navigate?: (url: string) => void }} deps
 */
async function refuseIfSalesPending(getQueued) {
  let queued;
  try {
    queued = await getQueued();
  } catch (_) {
    throw new QueueUnknownError();
  }
  const pending = (queued || []).filter((r) => r.status === 'queued' || r.status === 'failed').length;
  if (pending > 0) throw new PendingSalesError(pending);
}

export async function switchToShop(slug, {
  storage = window.localStorage,
  getQueued,
  logout,
  navigate = (url) => window.location.assign(url),
}) {
  const target = readKnownShops(storage).find((e) => e.slug === slug);
  // Only a shop the device has already been given the link for — the switcher never takes a slug
  // from anywhere else, but this is the check that makes that true rather than assumed.
  if (!target) throw new Error('unknown_shop');

  await refuseIfSalesPending(getQueued);

  // End the old shop's session on the server first — the login cookie is httpOnly and shared by
  // every window on this device, so only the server can clear it. Best-effort: with no session
  // (the PIN screen) or no network there is nothing to end, and the cookie expires on its own.
  try { await logout(); } catch (_) { /* best-effort */ }
  // Asked again, right before leaving: a sale that was still being submitted when Switch was
  // pressed can land in the offline queue while the logout call above is in flight (review
  // finding, PH-50 round 1). Refusing here costs a fresh sign-in to this same shop; navigating
  // would replay that sale into the other shop.
  try {
    await refuseIfSalesPending(getQueued);
  } catch (err) {
    // The server session is already gone, so do not leave this window believing it is signed
    // in (review finding, PH-50 round 2): drop the saved session, and say so (switchErrorText).
    try { storage.removeItem('session'); } catch (_) { /* the reload offered still asks for a PIN */ }
    if (err instanceof PendingSalesError) throw new PendingSalesError(err.count, true);
    throw new QueueUnknownError(true);
  }
  try {
    storage.removeItem('session');
    storage.removeItem('tenantConfig');
  } catch (_) { /* applyTenantResolution() clears both again on the switch it is about to see */ }

  navigate(`/s/${target.slug}/`);
}

// ── another window changed this device's shop ────────────────────────────────────────────────
//
// Two installed shop icons are the same web origin, so they share one login cookie and one
// localStorage. A window left open on shop A keeps rendering shop A while a second window opens
// shop B and signs in — and every later call from the first window would then run as shop B.
// App.jsx watches for the `storage` event on the shop key and stops that window; the flag below
// is how the reload it offers comes back to a PIN screen instead of silently reusing the session
// the OTHER window just created (sessionStorage is per window, so the other window is untouched).

export const FORCE_PIN_KEY = 'pos_force_pin';

const norm = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

// Which shop THIS window loaded for ('' = none named: the default shop, by Pattern C). Set once
// by App.jsx at load; changed afterwards only by recordBackfill() below, in this same window.
let windowSlug = '';
export const getWindowSlug = () => windowSlug;
export const setWindowSlug = (slug) => { windowSlug = norm(slug); };

// Settings.jsx fills in an EMPTY shop key from the signed-in session's own warehouse list — the
// normal state for a shop set up without an invite link. That is not a switch: a window with no
// shop key is, by Pattern C, on the default shop, and the value filled in IS that shop. Without
// telling the two apart, every such device's other windows were stopped mid-sale the first time
// anyone opened Settings (review finding, PH-50 round 1). The marker below is how: written just
// before the key, honoured only for a short while and only on a real `storage` event whose old
// value was genuinely empty — so an old marker can never wave a later, real switch through
// (review finding, round 2).
export const BACKFILL_KEY = 'pos_tenant_backfilled';
export const BACKFILL_FRESH_MS = 60 * 1000;

/** Settings' fill-in of an empty shop key. Throws if storage does (the caller's try/catch). */
export function recordBackfill(tid, storage = window.localStorage, now = Date.now()) {
  const v = norm(tid);
  storage.setItem(BACKFILL_KEY, JSON.stringify({ v, at: now }));
  storage.setItem('pos_tenant_id', tid);
  // This window named its own shop — it is not a switch for this window either.
  if (windowSlug === '') windowSlug = v;
}

function freshBackfill(storage, now) {
  try {
    const m = JSON.parse(storage.getItem(BACKFILL_KEY) || 'null');
    if (m && typeof m.v === 'string' && typeof m.at === 'number' && now - m.at >= 0 && now - m.at <= BACKFILL_FRESH_MS) {
      return norm(m.v);
    }
  } catch (_) { /* unreadable — treat as no marker */ }
  return '';
}

/**
 * Compares the device's shop key with the shop this window loaded for.
 * @param {string} winSlug  the shop this window is for ('' = the default shop)
 * @param {string|null|undefined} oldValue  the key's previous value from a `storage` event, or
 *   `undefined` when there is no event (a window coming back into view) — then nothing is adopted.
 * @returns {'same'|'adopt'|'changed'}  'adopt' = the empty key was just filled in with this
 *   window's own (default) shop; the caller takes the value as its own and carries on.
 */
export function classifyTenantChange(winSlug, oldValue, storage = window.localStorage, nowMs = Date.now()) {
  let now;
  try { now = norm(storage.getItem('pos_tenant_id')); } catch (_) { return 'changed'; }
  const win = norm(winSlug);
  if (now === win) return 'same';
  const eventSaysWasEmpty = oldValue === null || oldValue === '';
  if (win === '' && eventSaysWasEmpty) {
    const filled = freshBackfill(storage, nowMs);
    if (filled && filled === now) return 'adopt';
  }
  return 'changed';
}

/** classifyTenantChange() for a `storage` event; an event about any other key is 'same'. */
export function shopChangedElsewhere(event, windowSlug, storage = window.localStorage) {
  if (event.storageArea && event.storageArea !== storage) return 'same';
  // `key === null` is storage.clear() — the shop key went with everything else.
  if (event.key !== 'pos_tenant_id' && event.key !== null) return 'same';
  return classifyTenantChange(windowSlug, event.oldValue, storage);
}

/** Reload this window so it has to sign in again. Kept here so tests can replace it. */
export function reloadForPin(
  session = window.sessionStorage,
  reload = () => window.location.reload(),
  storage = window.localStorage,
) {
  try {
    session.setItem(FORCE_PIN_KEY, '1');
  } catch (_) {
    // Could not leave the per-window flag, so fail CLOSED the only other way available: drop the
    // device's saved session. That also sends the other window to its PIN screen — an annoyance,
    // where reloading straight into the other shop's session would be the bug itself.
    try { storage.removeItem('session'); } catch (__) { /* nothing more a client can do */ }
  }
  reload();
}

/** Reads and clears the flag reloadForPin() left for this window. Never throws. */
export function takeForcePinFlag(session = window.sessionStorage) {
  try {
    const on = session.getItem(FORCE_PIN_KEY) === '1';
    if (on) session.removeItem(FORCE_PIN_KEY);
    return on;
  } catch (_) {
    return false;
  }
}
